import {
	MatterMortgage,
	MatterMortgageWithAssociations,
	NetAdvancedAmount,
	NetAmountType,
	TransactionType,
} from '@models/mortgage';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import {
	CalculateNetAdvance,
	MortgageNetAdvanceCalculated,
} from 'src/app/shared/services/financialTotals/financial-totals.service';
import { ICON } from '@core/util/icon';
import { NetAmountTypeDescription } from '@core/util/mortgage';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { cloneDeep } from 'lodash';
import { InputComponent } from 'src/app/shared/atomic/atoms/input/input.component';
import { Component, Input } from '@angular/core';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { createId } from 'src/app/shared/core/util/file';

const Items: DropdownOption<NetAmountType>[] = [
	{
		name: NetAmountTypeDescription[NetAmountType.OTHER],
		value: NetAmountType.OTHER,
		icon: ICON.ADD,
		classes: ['font-bold'],
	},
	{
		name: NetAmountTypeDescription[NetAmountType.WIRE_FEE],
		value: NetAmountType.WIRE_FEE,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.LENDER_FEE],
		value: NetAmountType.LENDER_FEE,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.APPRAISAL_FEE],
		value: NetAmountType.APPRAISAL_FEE,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.HOLDBACK_FEE],
		value: NetAmountType.HOLDBACK_FEE,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.INSURANCE_APPLICATION_FEE],
		value: NetAmountType.INSURANCE_APPLICATION_FEE,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.INSURANCE_PREMIUM],
		value: NetAmountType.INSURANCE_PREMIUM,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.MISC_FEE],
		value: NetAmountType.MISC_FEE,
	},
	{
		name: NetAmountTypeDescription[NetAmountType.TAX_INS_PREM],
		value: NetAmountType.TAX_INS_PREM,
	},
	// { name: NetAmountTypeDescription[NetAmountType.OTHER_CREDIT], value: NetAmountType.OTHER_CREDIT } //MIGHT NEED IN FUTURE
];

export interface NetAmountModalData {
	mortgage: MatterMortgage;
}

@Component({
	selector: 'app-mortgage-net-amount',
	templateUrl: './net-amount.component.html',
	styleUrls: ['./net-amount.component.scss'],
	standalone: false,
})
export class NetAmountComponent {
	public ICON = ICON;
	public items = Items;
	public types = NetAmountType;
	public _mortgage: MatterMortgageWithAssociations;
	public netAdvancedAmount: MortgageNetAdvanceCalculated;
	public creditType = NetAmountType.OTHER_CREDIT;
	public lenderRetainedType = NetAmountType.RETAINED_BY_LENDER;
	public isEditDescriptionList: boolean[] = [];
	public showDropdownOptions = false;
	public get isRetainedByLenderOn() {
		return this._mortgage?.netAdvancedAmounts?.some(
			(x) => x.type === NetAmountType.RETAINED_BY_LENDER,
		);
	}

	public transactionTypes = TransactionType;

	@Input() isMortgageDrawerOpen = false;
	@Input() editable = true;

	@Input() showPrincipalAmount = true;
	@Input() showFinalCredit = true;
	@Input() set mortgage(mortgage: MatterMortgageWithAssociations) {
		this._mortgage = cloneDeep(mortgage);
		if (mortgage) {
			this.netAdvancedAmount = CalculateNetAdvance(this._mortgage);
		}
		this.isEditDescriptionList = [];
		this._mortgage?.netAdvancedAmounts?.forEach((netAdvancedAmount) => {
			this.isEditDescriptionList.push(!netAdvancedAmount?.description);
		});
	}
	@Input() forceErrorState = false;

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private analytics: AnalyticsService,
	) {}

	itemsTrackBy(index: number) {
		return index;
	}

	toggleDropdown() {
		if (this.isMortgageDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_ADD_ITEM_BUTTON_CLICK,
			);
		}
		this.showDropdownOptions = !this.showDropdownOptions;
	}

	hideDropdown() {
		this.showDropdownOptions = false;
	}

	setOverride(value: number) {
		if (this.isMortgageDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_NET_ADVANCE_AMOUNT_CHANGE,
			);
		}
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedOverride: value,
		});
	}

	toggleNetAdvanced() {
		if (this.isMortgageDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_SET_TO_CALCULATED_CLICK,
			);
		}
		const items = this._mortgage?.netAdvancedAmounts?.slice(1);
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedOverride: null,
			...(this.isRetainedByLenderOn && { netAdvancedAmounts: items }),
		});
	}

	onSelect(item: DropdownResult<DropdownOption<NetAmountType>>) {
		this.addItem(item.result.value);
	}

	toggleRetainedByLenderSwitch(event: boolean) {
		if (this.isMortgageDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_AUTO_CALCULATE_RETAINED_TOGGLE_CLICK,
			);
		}
		if (event) {
			this.addRetainedByLender();
		} else {
			this.removeRetainedByLender();
		}
	}

	addRetainedByLender() {
		const items: NetAdvancedAmount[] = [];
		items.push({
			id: createId(),
			description: 'Retained by lender',
			amount: 0,
			calculated: true,
			type: NetAmountType.RETAINED_BY_LENDER,
		});

		items.push(
			...(this._mortgage?.netAdvancedAmounts?.filter(
				(x) => x.type !== NetAmountType.RETAINED_BY_LENDER,
			) ?? []),
		);

		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedAmounts: items,
		});
	}

	removeRetainedByLender() {
		const items = this._mortgage?.netAdvancedAmounts?.slice(1);
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedAmounts: items,
		});
	}

	removeItem(index: number) {
		if (this.isMortgageDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_DELETE_DEDUCTION_ITEM_CLICK,
			);
		}
		const { netAdvancedAmounts } = this._mortgage;
		netAdvancedAmounts.splice(index, 1);
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedAmounts,
		});
	}

	updateNetAdvancedAmounts() {
		const { netAdvancedAmounts } = this._mortgage;
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedAmounts,
		});
	}

	onDescriptionSave(index: number, amountInput: InputComponent) {
		const { netAdvancedAmounts } = this._mortgage;
		netAdvancedAmounts[index].description.trim();
		this.updateNetAdvancedAmounts();
		setTimeout(() => {
			amountInput.inputElementRef.nativeElement.focus();
		});
	}

	editRetainedByLender() {
		this.isEditDescriptionList[0] = true;
	}

	cancelEditRetainedByLender() {
		this.isEditDescriptionList[0] = false;
	}

	getPlaceholder(type: NetAmountType) {
		return NetAmountTypeDescription[type];
	}

	public addItem(type: NetAmountType): void {
		if (this.isMortgageDrawerOpen && type === NetAmountType.OTHER) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_ADD_CUSTOM_ITEM_CLICK,
			);
		}
		const netAdvancedAmounts = this._mortgage.netAdvancedAmounts ?? [];
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			netAdvancedAmounts: [
				...netAdvancedAmounts,
				{
					id: createId(),
					description: [
						NetAmountType.OTHER,
						NetAmountType.OTHER_CREDIT,
					].includes(type)
						? ''
						: NetAmountTypeDescription[type],
					amount: 0,
					type,
				},
			],
		});
	}

	setAmount(value: number): void {
		if (this.isMortgageDrawerOpen) {
			this.analytics.logEvent(
				AnalyticsEvent.MORTGAGE_DRAWER_PRINCIPAL_AMOUNT_CHANGE,
			);
		}
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { amount: value });
	}

	calculateAmountDisplayValue(type: NetAmountType, amount: number): number {
		return (type === NetAmountType.OTHER_CREDIT ? -1 : 1) * amount;
	}

	isNullOrUndefined = (val) => val === null || val === undefined;
	protected readonly NetAmountType = NetAmountType;
}
